import { useEffect, useRef, type RefObject } from 'react';
import { useOverlay } from './overlay.js';

/**
 * The chrome every dialog shares: initial focus, a Tab trap, Esc to close, and a locked page
 * behind it.
 *
 * The subtle part is what this effect may depend on. Callers pass `onClose` as an inline arrow,
 * so it is a different function on every render; if the effect depended on it, every keystroke
 * in a field would tear the dialog's chrome down and set it up again — which re-runs the initial
 * focus and throws the caret back to the first field after each character. The handler is read
 * through a ref instead, and the effect depends only on whether the dialog is open.
 */
export function useDialogChrome({
  open,
  onClose,
  root,
  pickInitial,
}: {
  open: boolean;
  onClose: () => void;
  root: RefObject<HTMLElement | null>;
  /** Which element takes focus when the dialog opens; falls back to the first focusable. */
  pickInitial?: (el: HTMLElement) => HTMLElement | null | undefined;
}): void {
  const close = useRef(onClose);
  close.current = onClose;
  const pick = useRef(pickInitial);
  pick.current = pickInitial;
  useOverlay(open);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const el = root.current;
    const focusables = () =>
      Array.from(
        el?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
    const initial =
      (el && el.querySelector<HTMLElement>('[data-autofocus]')) ??
      (el && pick.current?.(el)) ??
      focusables()[0] ??
      el;
    initial?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const first = list[0]!;
      const last = list[list.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open, root]);
}
